import { useState } from "react"
import { Plus, Folder, User, BarChart3, Type } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import type { ThemeEvidence } from "@/stores/strategy-store"

interface AddEvidencePopoverProps {
  onAdd: (evidence: ThemeEvidence) => void
}

type EvidenceType = ThemeEvidence['type']

const evidenceTypes: { type: EvidenceType; label: string; icon: typeof Folder; placeholder: string; fields: string[] }[] = [
  { type: 'project', label: 'Project', icon: Folder, placeholder: 'e.g., Pacific Highway Upgrade — $12M, completed Oct 2024', fields: ['detail'] },
  { type: 'personnel', label: 'Personnel', icon: User, placeholder: 'e.g., Sarah Chen — PM, 18 years bridge experience', fields: ['detail'] },
  { type: 'statistic', label: 'Statistic', icon: BarChart3, placeholder: 'e.g., 100% on-time delivery across 47 projects, last 3 years', fields: ['detail'] },
  { type: 'freetext', label: 'Free Text', icon: Type, placeholder: 'Any supporting evidence...', fields: ['detail'] },
]

export function AddEvidencePopover({ onAdd }: AddEvidencePopoverProps) {
  const [open, setOpen] = useState(false)
  const [selectedType, setSelectedType] = useState<EvidenceType | null>(null)
  const [detail, setDetail] = useState('')

  const handleAdd = () => {
    if (!selectedType || !detail.trim()) return
    onAdd({ type: selectedType, detail: detail.trim() })
    setDetail('')
    setSelectedType(null)
    setOpen(false)
  }

  const handleCancel = () => {
    setSelectedType(null)
    setDetail('')
  }

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) handleCancel() }}>
      <PopoverTrigger asChild>
        <button type="button" className="inline-flex items-center gap-1.5 text-meta hover:text-foreground">
          <Plus className="h-3 w-3" />
          Add evidence
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 p-0">
        {!selectedType ? (
          <div className="py-1">
            {evidenceTypes.map(et => {
              const Icon = et.icon
              return (
                <button
                  key={et.type}
                  type="button"
                  onClick={() => setSelectedType(et.type)}
                  className="w-full text-left px-3 py-2 text-xs hover:bg-muted flex items-center gap-2"
                >
                  <Icon className="h-3.5 w-3.5 text-muted-foreground" />
                  {et.label}
                </button>
              )
            })}
          </div>
        ) : (
          <div className="p-3 space-y-3">
            <div className="text-xs font-medium capitalize">{selectedType} evidence</div>
            <Input
              placeholder={evidenceTypes.find(t => t.type === selectedType)?.placeholder}
              value={detail}
              onChange={(e) => setDetail(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
              autoFocus
              className="text-xs"
            />
            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={handleCancel} className="h-7 text-xs">Cancel</Button>
              <Button size="sm" onClick={handleAdd} disabled={!detail.trim()} className="h-7 text-xs">Add</Button>
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  )
}
